import { useQuery } from "@tanstack/react-query";
import { aiManagerApi } from "@/api";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import {
  Activity, Shield, Phone, GitBranch, BarChart3, ListChecks,
  AlertTriangle, CheckCircle2, XCircle, Clock, Wifi, WifiOff,
} from "lucide-react";

function timeAgo(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ja });
  } catch {
    return iso;
  }
}

// --- Health Check Card ---
function HealthCard({ health, alerts }: { health: Record<string, boolean>; alerts: string[] }) {
  const checks = Object.entries(health);
  const ok = checks.filter(([, v]) => v).length;
  const total = checks.length;
  const allOk = ok === total;

  const labels: Record<string, string> = {
    ai_caller_api: "AI Caller API",
    queue_healthy: "キュー正常",
    livekit_agent: "LiveKit Agent",
    asterisk: "Asterisk PBX",
    disk: "ディスク",
  };

  return (
    <div className={`rounded-lg border p-4 ${allOk ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Shield className={`w-5 h-5 ${allOk ? "text-green-600" : "text-red-600"}`} />
        <h3 className="font-semibold">ヘルスチェック</h3>
        <span className={`ml-auto text-sm font-medium ${allOk ? "text-green-700" : "text-red-700"}`}>
          {ok}/{total} OK
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {checks.map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5 text-sm">
            {val ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
            <span className={val ? "text-green-800" : "text-red-800"}>{labels[key] || key}</span>
          </div>
        ))}
      </div>
      {alerts.length > 0 && (
        <div className="mt-3 space-y-1">
          {alerts.map((a, i) => (
            <div key={i} className="flex items-start gap-1.5 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Quality Monitor Card ---
function QualityCard({ quality }: { quality: any }) {
  const m = quality?.metrics || {};
  const alerts = quality?.alerts || [];
  const hasAlerts = alerts.length > 0;
  const qualityFlags = m.quality_flags || {};
  const flagLabels: Record<string, string> = {
    LONG_NO_ANSWER: "長時間不応答",
    NO_ANSWER_WITH_USER_TURN: "発話あり不応答",
    CALLBACK_WEAK_EVIDENCE: "CB根拠弱い",
    CALLBACK_ACK_ONLY: "相槌のみCB",
    STT_DROPPED_OR_STALE: "STT欠落",
    BOT_DETECTED: "AI受付疑い",
    IVR_MISCLASSIFIED: "IVR誤分類",
    ANSWERED_WITHOUT_USER_TURN: "発話なし応答",
    TERMINATED_ON_ACK_ONLY: "相槌終話",
  };
  const visibleFlags = Object.entries(qualityFlags)
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 6);

  return (
    <div className={`rounded-lg border p-4 ${hasAlerts ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className={`w-5 h-5 ${hasAlerts ? "text-amber-600" : "text-slate-600"}`} />
        <h3 className="font-semibold">品質監視</h3>
        <span className="ml-auto text-xs text-slate-500">
          {quality?.lastCheck ? timeAgo(quality.lastCheck) : "未実行"}
        </span>
      </div>
      {Object.keys(m).length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {m.total != null && (
            <div>
              <div className="text-slate-500">直近件数</div>
              <div className="text-lg font-semibold">{m.total}</div>
            </div>
          )}
          {m.answer_rate != null && (
            <div>
              <div className="text-slate-500">応答率</div>
              <div className="text-lg font-semibold">{(m.answer_rate * 100).toFixed(0)}%</div>
            </div>
          )}
          {m.stt_issue_rate != null && (
            <div>
              <div className="text-slate-500">STT問題率</div>
              <div className={`text-lg font-semibold ${m.stt_issue_rate > 0.15 ? "text-red-600" : ""}`}>
                {(m.stt_issue_rate * 100).toFixed(0)}%
              </div>
            </div>
          )}
          {m.ghost_stt != null && (
            <div>
              <div className="text-slate-500">ゴーストSTT</div>
              <div className={`text-lg font-semibold ${m.ghost_stt > 0 ? "text-red-600" : ""}`}>
                {m.ghost_stt}件
              </div>
            </div>
          )}
          {m.callback_weak_evidence != null && (
            <div>
              <div className="text-slate-500">CB根拠弱</div>
              <div className={`text-lg font-semibold ${m.callback_weak_evidence > 0 ? "text-red-600" : ""}`}>
                {m.callback_weak_evidence}件
              </div>
            </div>
          )}
          {m.no_answer_with_user_turn != null && (
            <div>
              <div className="text-slate-500">発話あり不応答</div>
              <div className={`text-lg font-semibold ${m.no_answer_with_user_turn > 0 ? "text-red-600" : ""}`}>
                {m.no_answer_with_user_turn}件
              </div>
            </div>
          )}
          {m.bot_detected != null && (
            <div>
              <div className="text-slate-500">AI受付疑い</div>
              <div className={`text-lg font-semibold ${m.bot_detected > 0 ? "text-amber-600" : ""}`}>
                {m.bot_detected}件
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500">データなし</p>
      )}
      {visibleFlags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {visibleFlags.map(([flag, count]) => (
            <span key={flag} className="rounded border border-amber-200 bg-white px-2 py-1 text-xs font-medium text-amber-800">
              {flagLabels[flag] || flag}: {String(count)}
            </span>
          ))}
        </div>
      )}
      {hasAlerts && (
        <div className="mt-3 space-y-1">
          {alerts.map((a: string, i: number) => (
            <div key={i} className="flex items-start gap-1.5 text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Test Call History ---
function TestHistoryCard({ tests, lastTest }: { tests: any[]; lastTest: string | null }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Phone className="w-5 h-5 text-slate-600" />
        <h3 className="font-semibold">テスト架電</h3>
        <span className="ml-auto text-xs text-slate-500">
          最終: {lastTest ? timeAgo(lastTest) : "未実行"}
        </span>
      </div>
      {tests.length > 0 ? (
        <div className="space-y-2">
          {tests.slice(-10).reverse().map((t: any, i: number) => {
            const allPass = t.passed === t.total;
            return (
              <div key={i} className="flex items-center gap-2 text-sm">
                {allPass ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <span className="font-mono text-xs text-slate-500 w-32">{t.timestamp}</span>
                <span className={`font-medium ${allPass ? "text-green-700" : "text-amber-700"}`}>
                  {t.passed}/{t.total} pass
                </span>
                <span className="text-slate-600">{t.outcome}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-slate-500">テスト履歴なし</p>
      )}
    </div>
  );
}

// --- Deploy Status ---
function DeployCard({ lastDeploy, pendingDeploy }: { lastDeploy: string | null; pendingDeploy: string | null }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <GitBranch className="w-5 h-5 text-slate-600" />
        <h3 className="font-semibold">デプロイ</h3>
      </div>
      <div className="text-sm space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-slate-500 w-20">最終:</span>
          <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{lastDeploy || "—"}</code>
        </div>
        {pendingDeploy && (
          <div className="flex items-center gap-2 text-amber-700">
            <span className="w-20">保留中:</span>
            <code className="text-xs bg-amber-100 px-1.5 py-0.5 rounded">{pendingDeploy}</code>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Action Log ---
function ActionLog({ actions }: { actions: any[] }) {
  if (!actions.length) return null;

  const typeIcons: Record<string, string> = {
    health: "🏥",
    quality: "📊",
    test: "📞",
    deploy: "📦",
    alert: "🚨",
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-5 h-5 text-slate-600" />
        <h3 className="font-semibold">アクションログ</h3>
      </div>
      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {actions.map((a: any, i: number) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <span>{typeIcons[a.type] || "📝"}</span>
            <span className="font-mono text-xs text-slate-400 w-14 shrink-0">
              {a.time?.slice(11, 16) || ""}
            </span>
            <span className="text-slate-700">{a.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- List Recommendation ---
function ListRecommendation({ rec }: { rec: any }) {
  if (!rec) return null;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold">リスト推奨</h3>
      </div>
      <div className="text-sm space-y-2 text-blue-900">
        {rec.bestAreas && (
          <div><span className="font-medium">優先エリア:</span> {rec.bestAreas.join(", ")}</div>
        )}
        {rec.bestHours && (
          <div><span className="font-medium">優先時間帯:</span> {rec.bestHours.map((h: number) => `${h}時`).join(", ")}</div>
        )}
        {rec.avoidAreas && (
          <div><span className="font-medium">避けるべき:</span> {rec.avoidAreas.join(", ")}</div>
        )}
        {rec.avoidHours && (
          <div><span className="font-medium">避ける時間帯:</span> {rec.avoidHours.map((h: number) => `${h}時`).join(", ")}</div>
        )}
        {rec.notes && rec.notes.map((n: string, i: number) => (
          <div key={i} className="text-blue-700">- {n}</div>
        ))}
      </div>
    </div>
  );
}

// --- Main Page ---
export default function AiManager() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["ai-manager-status"],
    queryFn: () => aiManagerApi.status(),
    refetchInterval: 30_000, // 30秒ごとに自動更新
  });

  if (isLoading) return <div className="p-6 text-slate-500">読み込み中...</div>;
  if (error) return <div className="p-6 text-red-500">エラー: {String(error)}</div>;

  const s = data || {};

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Activity className="w-6 h-6 text-indigo-600" />
        <h1 className="text-xl font-bold">AI Manager</h1>
        <div className="ml-auto flex items-center gap-2">
          {s.connected ? (
            <div className="flex items-center gap-1.5 text-green-600 text-sm">
              <Wifi className="w-4 h-4" />
              <span>接続中</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-slate-400 text-sm">
              <WifiOff className="w-4 h-4" />
              <span>{s.stale ? "古いデータ" : "未接続"}</span>
            </div>
          )}
          {s.lastUpdate && (
            <span className="text-xs text-slate-400">
              <Clock className="w-3 h-3 inline mr-1" />
              {timeAgo(s.lastUpdate)}
            </span>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="space-y-4">
        {/* Row 1: Health + Quality */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <HealthCard health={s.health || {}} alerts={s.healthAlerts || []} />
          <QualityCard quality={s.quality} />
        </div>

        {/* Row 2: Tests + Deploy */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TestHistoryCard tests={s.testHistory || []} lastTest={s.lastTestCall} />
          <DeployCard lastDeploy={s.lastDeploy} pendingDeploy={s.pendingDeploy} />
        </div>

        {/* Row 3: List Recommendation */}
        <ListRecommendation rec={s.listRecommendation} />

        {/* Row 4: Action Log */}
        <ActionLog actions={s.actions || []} />
      </div>
    </div>
  );
}
